import { useEffect, useMemo, useState } from "react";
import { RackFrameLayer } from "./RackFrameLayer";
import { getStructureVolumes, type RackVolume } from "../api/rest";
import { useUIStore } from "../stores/uiStore";
import { isActiveQuery } from "./productSearch";
import type { RackFrameOptions } from "./rackFrame";
import { structureTopOf } from "./rackVolume";

interface Props {
  siteId: string;
  warehouseId: string;
}

/**
 * Dev knobs via URL query, so the rack look can be tuned + isolated WITHOUT a rebuild:
 *   ?rackOnly=JY01-00A,JY01-00B   → draw only those addresses (isolate a bay)
 *   ?aisle=y                      → force the aisle axis (auto-detect is unreliable on a subset)
 *   ?frameEvery=3 &runBreakM=1.5 &depthClusterGapM=2  → RackFrameOptions passthrough
 * No params → normal render.
 */
function rackDebug(): { only: Set<string> | null; options: RackFrameOptions } {
  const q = new URLSearchParams(window.location.search);
  const only = q.get("rackOnly");
  // A malformed knob (?frameEvery=abc) must fall back to the default, not inject NaN
  // into the geometry (NaN survives Math.max/floor and voids every beam span).
  const num = (k: string) => {
    const raw = q.get(k);
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };
  const aisle = q.get("aisle");
  return {
    only: only ? new Set(only.split(",").map((s) => s.trim()).filter(Boolean)) : null,
    options: {
      aisle: aisle === "x" || aisle === "y" ? aisle : undefined,
      frameEvery: num("frameEvery"),
      runBreakM: num("runBreakM"),
      depthClusterGapM: num("depthClusterGapM"),
    },
  };
}

/**
 * ADR 170/185: fetches the warehouse's inferred rack volumes and mounts the kit-of-parts
 * frame layer. Run-independent — it reads the canonical structure graph, not an inventory run
 * (unlike OccupancyLayerContainer). One fetch on mount/change. Mounted/torn down by Scene.tsx
 * from `layers.rackVolumes`; keyed on site+warehouse at the call site.
 */
export function RackVolumesLayerContainer({ siteId, warehouseId }: Props) {
  const [volumes, setVolumes] = useState<RackVolume[] | null>(null);
  // ADR 190: a height edit in the bin inspector reshapes the prisms — refetch on bump.
  const dataVersion = useUIStore((s) => s.sceneDataVersion);
  // ADR 196: while a product search filters the occupancy layer, the steel fades
  // too — the found loads must read through the racks. Gated on the occupancy
  // layer being on: with no highlights on screen a ghosted rack means nothing.
  const ghost = useUIStore((s) => isActiveQuery(s.productQuery) && s.layers.occupancy);
  const setStructureTop = useUIStore((s) => s.setStructureTop);

  useEffect(() => {
    let cancelled = false;
    getStructureVolumes(siteId, warehouseId)
      .then((r) => {
        if (cancelled) return;
        setVolumes(r.volumes);
        // ADR 370: the walk's ceiling is the top of the racking.
        setStructureTop(warehouseId, structureTopOf(r.volumes));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[RackVolumes] structure/volumes fetch failed — rack layer hidden", err);
        setVolumes(null);
      });
    return () => { cancelled = true; };
  }, [siteId, warehouseId, dataVersion, setStructureTop]);

  const { shown, options } = useMemo(() => {
    if (!volumes) return { shown: null as RackVolume[] | null, options: {} as RackFrameOptions };
    const { only, options } = rackDebug();
    return { shown: only ? volumes.filter((v) => only.has(v.address)) : volumes, options };
  }, [volumes]);

  if (!shown || shown.length === 0) return null;
  return <RackFrameLayer volumes={shown} options={options} ghost={ghost} />;
}
